import React from "react";
import moment from "moment";
import { StatusBadge, BadgeTone } from "../../../atoms/StatusBadge";
import {
  AttachWarning,
  ClientSubscriptionStatus,
  LedgerLine,
  LedgerState,
  PartnerProgram,
  SponsoredPlan,
} from "../../../../services/partnerAdmin";
import { formatPayModDate } from "../../../../services/planChange";

const PROGRAMS: Record<
  PartnerProgram,
  { label: string; tone: BadgeTone; hint?: string }
> = {
  distributor: { label: "Distributor", tone: "blue" },
  distributor_pending: {
    label: "Distributor, contract pending",
    tone: "warning",
  },
  affiliate: { label: "Affiliate", tone: "gray" },
  unset: { label: "No program yet", tone: "gray" },
  unreadable: {
    label: "Data unavailable",
    tone: "warning",
    hint: "This partner's data could not be read, so its program, contract and client counts are unknown. It is not a partner without a program. Refresh to try again.",
  },
};

const SUBSCRIPTIONS: Record<
  ClientSubscriptionStatus,
  { label: string; tone: BadgeTone; hint: string }
> = {
  ACTIVE: {
    label: "Active",
    tone: "success",
    hint: "The client has a paid plan in good standing.",
  },
  UNPAID: {
    label: "Unpaid",
    tone: "failure",
    hint: "A payment is overdue: the client has a plan but the last due invoice went unpaid.",
  },
  ENDED: {
    label: "Last known period ended",
    tone: "gray",
    hint: "The last paid period has ended and no renewal is known.",
  },
  NONE: {
    label: "No paid plan",
    tone: "gray",
    hint: "The account is on the free plan: it never paid, or its plan is not sponsored by the distributor.",
  },
  UNKNOWN: {
    label: "Payment history unavailable",
    tone: "warning",
    hint: "The client's payment history could not be read, so its status is unknown.",
  },
  DELETED: {
    label: "Account deleted",
    tone: "gray",
    hint: "The client's account has been deleted.",
  },
};

const TIERS: Record<string, string> = {
  free: "Free",
  premium: "Premium",
  enterprise: "Enterprise",
  large_business: "Large Business",
  promo_enterprise_trial: "Enterprise trial",
};

export const programBadge = (program: PartnerProgram) => {
  const { label, tone, hint } = PROGRAMS[program] ?? PROGRAMS.unset;
  return (
    <StatusBadge color={tone} title={hint} className="w-fit whitespace-nowrap">
      {label}
    </StatusBadge>
  );
};

/** A count the back end could not read: unknown, and never shown as zero or left blank. */
export const clientCount = (value: number | null) =>
  typeof value === "number" ? (
    value
  ) : (
    <span className="font-normal text-gray-500">Unknown</span>
  );

export const subscriptionBadge = (status: ClientSubscriptionStatus) => {
  const { label, tone, hint } = SUBSCRIPTIONS[status] ?? SUBSCRIPTIONS.UNKNOWN;
  return (
    <StatusBadge color={tone} title={hint} className="w-fit whitespace-nowrap">
      {label}
    </StatusBadge>
  );
};

const CONTRACT_STATUSES: Record<
  string,
  { label: string; tone: BadgeTone; hint: string }
> = {
  pending: {
    label: "Sent, not opened",
    tone: "warning",
    hint: "The distributor contract was sent by e-signature but the partner has not opened it yet.",
  },
  viewed: {
    label: "Opened, not signed",
    tone: "warning",
    hint: "The partner opened the contract but has not signed it yet.",
  },
  review: {
    label: "Opened, not signed",
    tone: "warning",
    hint: "The partner opened the contract but has not signed it yet.",
  },
  signed: {
    label: "Signed",
    tone: "success",
    hint: "The partner signed the distributor contract.",
  },
  declined: {
    label: "Declined",
    tone: "failure",
    hint: "The partner declined the contract. Resend a new one to continue.",
  },
  expired: {
    label: "Link expired",
    tone: "failure",
    hint: "The signing link reached its 30-day validity without being signed. Resend a new one.",
  },
  cancelled: {
    label: "Cancelled",
    tone: "gray",
    hint: "The signing request was cancelled.",
  },
  not_sent: {
    label: "Not sent",
    tone: "gray",
    hint: "No readable e-signature request was found for this partner: the contract was never sent, or its signing request is missing or unreadable. It does not read the 'Contract signed' date, so a signed partner can still show this if the e-signature record cannot be read.",
  },
  not_distributor_contract: {
    label: "Not the distributor contract",
    tone: "warning",
    hint: "An e-signature request exists but it does not use the current distributor contract template.",
  },
  unavailable: {
    label: "State unavailable",
    tone: "warning",
    hint: "The e-signature service could not be reached to read the contract status.",
  },
};

export const contractBadge = (status: string) => {
  const { label, tone, hint } =
    CONTRACT_STATUSES[status] ?? CONTRACT_STATUSES.unavailable;
  return (
    <StatusBadge color={tone} title={hint} className="w-fit whitespace-nowrap">
      {label}
    </StatusBadge>
  );
};

const PAYOUT_METHODS: Record<string, string> = {
  bank: "Bank transfer",
  stripe_connect: "Stripe Connect",
  paypal: "PayPal",
  crypto: "Cryptocurrency (withdrawn)",
};

export const payoutMethodLabel = (method: string | null) =>
  method ? PAYOUT_METHODS[method] ?? "Other" : "Not set";

const LEVELS: Record<string, string> = {
  STANDARD: "Standard",
  BRONZE: "Bronze",
  SILVER: "Silver",
  GOLD: "Gold",
};

export const levelLabel = (level: string | null) =>
  level ? LEVELS[level] ?? "Other" : "None";

export const tierLabel = (tier: string | null) =>
  tier ? TIERS[tier] ?? "Other plan" : "None";

/** The paid plans the partner program knows, as the back end's PLAN_CATALOG: the plan a line added by hand may name. */
export const PROGRAM_PLANS: {
  code: string;
  tier: string;
  interval: "month" | "annual";
  currency: string;
}[] = [
  {
    code: "UMD_PREMIUM_BUSINESS_MONTH",
    tier: "premium",
    interval: "month",
    currency: "USD",
  },
  {
    code: "UMD_PREMIUM_BUSINESS_ANNUAL",
    tier: "premium",
    interval: "annual",
    currency: "USD",
  },
  {
    code: "SUMOM_8PS8HPOLTF",
    tier: "premium",
    interval: "month",
    currency: "EUR",
  },
  {
    code: "SUMOA_EUEIVLUAUW",
    tier: "premium",
    interval: "annual",
    currency: "EUR",
  },
  {
    code: "SUMOM_MHMYDHE9NH",
    tier: "premium",
    interval: "month",
    currency: "CHF",
  },
  {
    code: "SUMOA_HOYZZNJVYT",
    tier: "premium",
    interval: "annual",
    currency: "CHF",
  },
  {
    code: "UMD_ENTREPRISE_BUSINESS_MONTH",
    tier: "enterprise",
    interval: "month",
    currency: "USD",
  },
  {
    code: "UMD_ENTREPRISE_BUSINESS_ANNUAL",
    tier: "enterprise",
    interval: "annual",
    currency: "USD",
  },
  {
    code: "SUMOM_GYASCZ0RSW",
    tier: "enterprise",
    interval: "month",
    currency: "EUR",
  },
  {
    code: "SUMOA_VJTRNHTBIB",
    tier: "enterprise",
    interval: "annual",
    currency: "EUR",
  },
  {
    code: "SUMOM_JVXKQNIFEN",
    tier: "enterprise",
    interval: "month",
    currency: "CHF",
  },
  {
    code: "SUMOA_BOAXCZVLIQ",
    tier: "enterprise",
    interval: "annual",
    currency: "CHF",
  },
  {
    code: "UMD_LARGE_BUSINESS_MONTH",
    tier: "large_business",
    interval: "month",
    currency: "USD",
  },
  {
    code: "UMD_LARGE_BUSINESS_ANNUAL",
    tier: "large_business",
    interval: "annual",
    currency: "USD",
  },
  {
    code: "SUMOM_A8NQTU2UIB",
    tier: "large_business",
    interval: "month",
    currency: "EUR",
  },
  {
    code: "SUMOA_HZVGJGKYLM",
    tier: "large_business",
    interval: "annual",
    currency: "EUR",
  },
  {
    code: "SUMOM_7YV3KX83JA",
    tier: "large_business",
    interval: "month",
    currency: "CHF",
  },
  {
    code: "SUMOA_ZEDIVSBYFC",
    tier: "large_business",
    interval: "annual",
    currency: "CHF",
  },
];

export const planLabel = (plan: { tier: string; interval: string }) =>
  `${tierLabel(plan.tier)}, ${
    plan.interval === "month" ? "monthly" : "annual"
  }`;

export const formatDay = (value: string | null) =>
  value ? moment(value).format("ll") : "None";

export const formatPayModDay = (value: string | null) =>
  formatPayModDate(value, "ll", "None");

export const partnerName = (p: {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  partner_id: string;
}) =>
  [p.first_name, p.last_name].filter(Boolean).join(" ") ||
  p.email ||
  p.partner_id;

export const warningText = (warning: AttachWarning) => {
  switch (warning.code) {
    case "ALREADY_PAID":
      return "This account has already paid for a subscription. The contract only covers free accounts that never paid.";
    case "PAYMENT_HISTORY_UNKNOWN":
      return "The payment history of this account could not be read, so it may already have paid.";
    case "ATTACHED_ELSEWHERE":
      return `This account is attached to partner ${warning.partner_id}. Forcing moves it here and marks it detached there.`;
    default:
      return "Unexpected warning.";
  }
};

const LEDGER_STATES: Record<
  LedgerState,
  { label: string; tone: BadgeTone; hint: string }
> = {
  pending: {
    label: "Pending",
    tone: "gray",
    hint: "Recorded but not payable yet: a line becomes due 30 days after the client's payment, a grace window in case the client is refunded.",
  },
  due: {
    label: "Due",
    tone: "blue",
    hint: "The grace window has passed: SUM owes this amount and it counts toward the payable balance.",
  },
  on_hold: {
    label: "On hold, disputed",
    tone: "warning",
    hint: "The client's payment is disputed at Stripe: the line is frozen until the dispute is settled.",
  },
  canceled: {
    label: "Cancelled",
    tone: "gray",
    hint: "Withdrawn: the client was refunded, or a line added by hand was taken back. It will not be paid.",
  },
  paid: {
    label: "Paid",
    tone: "success",
    hint: "Already paid out to the partner in a payout.",
  },
};

export const ledgerStateBadge = (state: LedgerState) => {
  const { label, tone, hint } = LEDGER_STATES[state] ?? LEDGER_STATES.pending;
  return (
    <StatusBadge color={tone} title={hint} className="w-fit whitespace-nowrap">
      {label}
    </StatusBadge>
  );
};

const CONNECT_STATUSES: Record<string, { label: string; tone: BadgeTone }> = {
  ACTIVE: { label: "Active", tone: "success" },
  PENDING: { label: "Onboarding not finished", tone: "warning" },
  RESTRICTED: { label: "Checks under way", tone: "warning" },
  INACTIVE: { label: "Removed at Stripe", tone: "gray" },
  DISABLED: { label: "Disabled", tone: "failure" },
  REJECTED: { label: "Rejected by Stripe", tone: "failure" },
};

export const connectStatusBadge = (status: string) => {
  const { label, tone } = CONNECT_STATUSES[status] ?? {
    label: "Unknown",
    tone: "gray",
  };
  return (
    <StatusBadge color={tone} className="w-fit whitespace-nowrap">
      {label}
    </StatusBadge>
  );
};

export const ledgerKindLabel = (line: LedgerLine) => {
  if (line.kind === "clawback") return "Clawback of a refunded payment";
  if (line.kind === "commission")
    return line.program === "distributor"
      ? "Commission, client paying itself"
      : "Commission";
  return line.upgrade ? "Rebate, upgrade difference" : "Rebate";
};

export const formatAmount = (amount: number, currency: string) =>
  `${amount < 0 ? "-" : ""}${Math.abs(amount).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })} ${currency}`;

/** What the distributor pays for a client, in one line. */
export const sponsoredText = (plan: SponsoredPlan) => {
  const tier = tierLabel(plan.plan_tier);
  if (!plan.running)
    return `Paid by the distributor until ${formatPayModDay(
      plan.current_period_end
    )}, ended`;
  if (plan.auto_renew === false) {
    return `${tier} annual paid by the distributor, renewal off, ends ${formatPayModDay(
      plan.current_period_end
    )}`;
  }
  return `${tier} annual paid by the distributor, renews ${formatPayModDay(
    plan.current_period_end
  )}`;
};
